import { KdsModal, KdsText } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";

import { useAddress, useToasts } from "@/hooks";

import { type AddressDeleteModalProps } from "./AddressDeleteModal.types";

export const AddressDeleteModal = ({
  isOpen,
  addressId,
  onOpenChange,
  onDeleteCallback,
}: AddressDeleteModalProps) => {
  // Hooks
  const { t } = useTranslate();
  const { add } = useToasts();

  // Query hooks
  const { useDeleteAddress } = useAddress();
  const { mutateAsync: deleteAddress, isPending: isDeletingAddress } =
    useDeleteAddress();

  // Handle Delete Address
  const handleDeleteAddress = async () => {
    try {
      const response = await deleteAddress({ id: addressId });

      if (response.status === 204) {
        add({
          state: "success",
          title: t("addressDrawer.deleteAddressModal.toastSuccess"),
        });
        onDeleteCallback();
        onOpenChange();
      }
    } catch {
      add({
        state: "error",
        title: t("toast.generic.error"),
      });
    }
  };

  return (
    <KdsModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="sm"
      dialogProps={{
        dialogHeaderProps: {
          action: onOpenChange,
          title: t("addressDrawer.deleteAddressModal.title"),
        },
        content: (
          <KdsText>{t("addressDrawer.deleteAddressModal.content")}</KdsText>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: t("buttons.cancel"),
              variant: "outlined",
              color: "neutral",
              onPress: onOpenChange,
            },
            {
              children: t("buttons.delete"),
              color: "error",
              isLoading: isDeletingAddress,
              onPress: () => void handleDeleteAddress(),
            },
          ],
          type: "double",
        },
      }}
    />
  );
};
